import { useState, useEffect } from 'react';

export type CookieConsentType = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'partial';

const defaultConsent: CookieConsentType = {
  essential: true, // Always true, essential cookies cannot be disabled
  analytics: false,
  marketing: false,
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsentType>(defaultConsent);
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem('cookie-consent');
    const savedStatus = localStorage.getItem('consent-status');
    
    if (savedConsent && savedStatus) {
      setConsent(JSON.parse(savedConsent));
      setStatus(savedStatus as ConsentStatus);
      setBannerVisible(false);
    } else {
      setBannerVisible(true);
    }
  }, []);

  const acceptAll = () => {
    const fullConsent: CookieConsentType = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(fullConsent);
    setStatus('accepted');
    setBannerVisible(false);
    localStorage.setItem('cookie-consent', JSON.stringify(fullConsent));
    localStorage.setItem('consent-status', 'accepted');
    
    // Initialize Google Analytics if analytics consent is given
    if (fullConsent.analytics) {
      initializeGoogleAnalytics();
    }
  };

  const rejectAll = () => {
    const minimalConsent: CookieConsentType = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setConsent(minimalConsent);
    setStatus('rejected');
    setBannerVisible(false);
    localStorage.setItem('cookie-consent', JSON.stringify(minimalConsent));
    localStorage.setItem('consent-status', 'rejected');
  };

  const updateConsent = (newConsent: CookieConsentType) => {
    setConsent(newConsent);
    const newStatus: ConsentStatus = newConsent.analytics || newConsent.marketing ? 'partial' : 'rejected';
    setStatus(newStatus);
    setBannerVisible(false);
    localStorage.setItem('cookie-consent', JSON.stringify(newConsent));
    localStorage.setItem('consent-status', newStatus);
    
    // Initialize or disable Google Analytics based on consent
    if (newConsent.analytics) {
      initializeGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
  };

  const resetConsent = () => {
    setConsent(defaultConsent);
    setStatus('pending');
    setBannerVisible(true);
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('consent-status');
    disableGoogleAnalytics();
  };

  return {
    consent,
    status,
    bannerVisible,
    acceptAll,
    rejectAll,
    updateConsent,
    resetConsent,
  };
};

// Google Analytics integration functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const initializeGoogleAnalytics = () => {
  const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
  
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;

  // Create script elements if they don't exist
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', {
        anonymize_ip: true,
        cookie_flags: 'SameSite=Strict;Secure'
      });
    `;
    document.head.appendChild(script2);
  } else {
    // Re-enable tracking if scripts already exist
    window.gtag?.('consent', 'update', {
      analytics_storage: 'granted'
    });
  }
};

const disableGoogleAnalytics = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied'
    });
  }
};